import React from 'react';
import { MultiGrid, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import { DraggableTask, TaskDragOverlay } from './TaskBadge.tsx';
import { 
  SameDayReservations,
  FullWidthStackedReservations,
  CheckinOnlyReservation,
  CheckoutOnlyReservation,
  StayReservation,
} from './ReservationBadge.tsx';
import { CalendarSkeleton, CompactCalendarSkeleton } from './LoadingSkeleton.tsx';

interface Department {
  name: string;
  group: string;
}

// Droppable Cell Component
const DroppableCell = ({ id, children, activeTaskPropertyId }: { id: string; children: React.ReactNode; activeTaskPropertyId?: string }) => {
  const { isOver, setNodeRef } = useDroppable({
    id,
  });

  // Parse the cell's property ID
  const dropId = id.toString();
  const lastDashIndex = dropId.lastIndexOf('-');
  const secondLastDashIndex = dropId.lastIndexOf('-', lastDashIndex - 1);
  const thirdLastDashIndex = dropId.lastIndexOf('-', secondLastDashIndex - 1);
  const cellPropertyId = dropId.slice(0, thirdLastDashIndex);

  // Only show drag-over effect if hovering over same property as dragged task
  const showDragOver = isOver && activeTaskPropertyId && cellPropertyId === activeTaskPropertyId;

  return (
    <div 
      ref={setNodeRef}
      className={`date-cell-drop-zone ${showDragOver ? 'date-cell-drag-over' : ''}`}
    >
      {children}
    </div>
  );
};

interface PropertyCalendarGridProps {
  properties?: any[];
  reservations?: any[];
  tasks?: any[];
  departments?: Department[];
  startDate?: Date;
  endDate?: Date;
  bubbleInstance?: any;
  loading?: boolean;
  loadingType?: 'full' | 'compact';
}

// Helper functions for data processing
// YYYY-MM-DD from local date components only (no ISO/UTC, no timezone conversion)
const dateToLocalDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
// Returns an array of date objects
const generateDateColumns = (startDate?: Date, endDate?: Date) => {
  const dates: { date: Date; key: string; title: string; width: number }[] = [];
  
  const start = startDate || new Date();
  const end = endDate || (() => {
    const defaultEnd = new Date();
    defaultEnd.setDate(defaultEnd.getDate() + 30);
    return defaultEnd;
  })();
  
  let currentDate = new Date(start);
  
  while (currentDate <= end) {
    dates.push({
      date: new Date(currentDate),
      key: dateToLocalDateKey(currentDate),
      title: currentDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }),
      width: 80
    });
    // console.log('currentDate', currentDate);
    // console.log('adding date column', dateToLocalDateKey(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return dates;
};

// Filter reservations by property and date
const filterCellReservations = (reservations: any[], property: any, cellDate: Date) => {
  return reservations.filter(reservation => {
    const reservationProperty = reservation.property_id || reservation.property;
    const checkinDate = reservation.checkin_field;
    const checkoutDate = reservation.checkout_field;
    
    if (!checkinDate || !checkoutDate) return false;
    
    // const checkinDateKey = dateToLocalDateKey(checkinDate);
    // const checkoutDateKey = dateToLocalDateKey(checkoutDate);

    const isPropertyMatch = reservationProperty === property?.id;
    const isDateInRange = cellDate >= checkinDate.setHours(0, 0, 0, 0) && cellDate <= checkoutDate.setHours(0, 0, 0, 0);
    // console.log('cellDate', cellDate);
    // console.log('checkinDate', checkinDate);
    // console.log('checkoutDate', checkoutDate);
    return isPropertyMatch && isDateInRange;
  });
};

const filterCellTasks = (tasks: any[], property: any, cellDateKey: string) => {
  return tasks.filter(task => {
    const taskProperty = task.propertyId || task.property_id;
    const dueDate = task.dueDate || task.due_date;
    
    if (!dueDate) return false;
    
    const dueDateKey = dateToLocalDateKey(dueDate);
    // console.log('dueDate Task LOOK HERE', dueDate);
    // console.log('dueDate Task KEY LOOK HERE', dueDateKey);
    const isPropertyMatch = taskProperty === property?.id;
    const isDueDateMatch = dueDateKey === cellDateKey;
    
    return isPropertyMatch && isDueDateMatch;
  });
};

export function PropertyCalendarGrid({ 
  properties = [], 
  reservations = [], 
  tasks = [], 
  departments = [],
  startDate, 
  endDate,
  bubbleInstance,
  loading = true,
  loadingType = 'full'
}: PropertyCalendarGridProps) {
  
  // Get user's device timezone automatically
  const userTimezone = React.useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, []);
  
  // Log timezone information to check for discrepancies
  React.useEffect(() => {
    if (!loading && (reservations.length > 0 || tasks.length > 0)) {
      // System and User Timezone Info
      const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const systemOffset = new Date().getTimezoneOffset();
      const systemOffsetHours = -systemOffset / 60; // Convert to hours (negative because offset is inverted)

    }
  }, [loading, reservations, tasks, userTimezone]);
  
  // Show loading skeleton if loading
  if (loading) {
    if (loadingType === 'compact') {
      return <CompactCalendarSkeleton />;
    }
    
    const dateColumns = generateDateColumns(startDate, endDate);
    return (
      <CalendarSkeleton 
        propertyCount={properties.length || 5}
        dateCount={dateColumns.length || 7}
      />
    );
  }
  const [hoveredStayCell, setHoveredStayCell] = React.useState<string | null>(null);
  const [activeTask, setActiveTask] = React.useState<any>(null);
  const [localTasks, setLocalTasks] = React.useState(tasks);
  const [tooltipData, setTooltipData] = React.useState<{
    reservations: any[];
    type: string;
    show: boolean;
    x: number;
    y: number;
  } | null>(null);

  // Event handlers
  const handlePropertyClick = (property: any) => {
   //console.log('🎯 Property clicked:', property?.id);
    
    if (bubbleInstance?.publishState && bubbleInstance?.triggerEvent) {
      bubbleInstance.publishState('selected_property_id', property?.id || '');
      bubbleInstance.triggerEvent('property_clicked', () => {
        //console.log('✅ Property clicked event sent to Bubble');
      });
    } else {
      //console.log('⚠️ Bubble instance not available for property click');
    }
  };

  const handleReservationClick = (reservationData: any) => {
    // Handle multiple reservations (array) vs single reservation (object)
    if (Array.isArray(reservationData)) {
      // Multiple reservations - use existing event with comma-separated IDs
      const reservationIds = reservationData.map(r => r.reservation_id || r.id).filter(id => id);
      const reservationIdsString = reservationIds.join(',');
      //console.log('multiple_reservations_clicked', reservationIds);
      if (bubbleInstance?.publishState && bubbleInstance?.triggerEvent) {
        bubbleInstance.publishState('selected_reservation_ids', reservationIds);
        bubbleInstance.triggerEvent('multiple_reservations_clicked');
      }
    } else {
      // Single reservation - use existing logic
      //console.log('🎯 Reservation clicked:', reservationData.reservation_id);
      if (bubbleInstance?.publishState && bubbleInstance?.triggerEvent) {
        bubbleInstance.publishState('selected_reservation_id', reservationData.reservation_id || '');
        bubbleInstance.triggerEvent('reservation_clicked');
      }
    }
  };

  const handleAddTaskClick = (cellDateKey: string, propertyId: string) => {
    //console.log('🎯 Add task clicked for date:', cellDateKey, 'property:', propertyId);
    if (bubbleInstance?.publishState && bubbleInstance?.triggerEvent) {
      bubbleInstance.publishState('selected_date', cellDateKey);
      bubbleInstance.publishState('selected_property_id', propertyId || '');
      bubbleInstance.triggerEvent('add_task_requested');
    }
  };

  const handleTooltipHover = (reservations: any[], type: string, show: boolean, event?: React.MouseEvent) => {
    if (show && event) {
      setTooltipData({ 
        reservations, 
        type, 
        show, 
        x: event.clientX, 
        y: event.clientY 
      });
    } else {
      setTooltipData(null);
    }
  };
  
  // Update local tasks when props change
  React.useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);
  
  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  // Create cache for dynamic row heights with more stable default
  const cache = React.useMemo(
    () => new CellMeasurerCache({
      defaultHeight: 100, // Increased default to reduce flicker
      fixedWidth: true,
    }),
    []
  );

  // Only clear cache when structural changes occur
  const prevPropertiesLength = React.useRef(properties.length);
  const prevTasksPerProperty = React.useRef<Record<string, number>>({});
  
  React.useEffect(() => {
    // Only clear cache if row count changed or tasks per property changed significantly
    const propertiesChanged = properties.length !== prevPropertiesLength.current;
    
    // Check if task distribution per property changed
    const currentTasksPerProperty: Record<string, number> = {};
    localTasks.forEach(task => {
      const propertyId = task.propertyId || task.property_id || 'unknown';
      currentTasksPerProperty[propertyId] = (currentTasksPerProperty[propertyId] || 0) + 1;
    });
    
    const tasksDistributionChanged = Object.keys(currentTasksPerProperty).some(
      propertyId => Math.abs(
        (currentTasksPerProperty[propertyId] || 0) - 
        (prevTasksPerProperty.current[propertyId] || 0)
      ) > 2  // Only clear if more than 2 tasks added/removed per property
    );
    
    if (propertiesChanged || tasksDistributionChanged) {
      cache.clearAll();
      prevPropertiesLength.current = properties.length;
      prevTasksPerProperty.current = currentTasksPerProperty;
    }
  }, [properties.length, localTasks, cache]);

  // Handle drag start
  const handleDragStart = (event: any) => {
    const { active } = event;
    const taskData = active.data.current;
    setActiveTask(taskData);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const taskData = active.data.current;
    if (!taskData || !taskData.task) return;

    // Parse the droppable ID to extract property ID and date
    // ID format: propertyId-YYYY-MM-DD
    const dropId = over.id.toString();
    const lastDashIndex = dropId.lastIndexOf('-');
    const secondLastDashIndex = dropId.lastIndexOf('-', lastDashIndex - 1);
    const thirdLastDashIndex = dropId.lastIndexOf('-', secondLastDashIndex - 1);
    
    // Extract the date (last 10 characters: YYYY-MM-DD)
    const targetDate = dropId.slice(thirdLastDashIndex + 1);
    // Extract the property ID (everything before the date)
    const targetPropertyId = dropId.slice(0, thirdLastDashIndex);

    // Only allow date changes within the same property
    if (taskData.propertyId !== targetPropertyId) {
      return;
    }

    // Only update if dropped on a different date
    if (taskData.currentDate !== targetDate) {

      // Update the task's due date in the local state (keep same property)
      const updatedTasks = localTasks.map(task => {
        if ((task.task_id || task.id) === taskData.task.task_id) {
          return {
            ...task,
            dueDate: targetDate,
            due_date: targetDate
          };
        }
        return task;
      });

      // Update local state to move the task immediately
      setLocalTasks(updatedTasks);

      // Trigger Bubble event for task date change
      if (bubbleInstance?.publishState && bubbleInstance?.triggerEvent) {
        bubbleInstance.publishState('selected_date', targetDate);
        bubbleInstance.publishState('selected_task_id', taskData.task.task_id);
        bubbleInstance.triggerEvent('task_date_changed');
      }
    }
  };
  
  // RENDERING THE CELLS
  
  const dateColumns = generateDateColumns(startDate, endDate);
  const propertyColumnWidth = 200;

  const cellRenderer = ({ columnIndex, key, rowIndex, style, parent }: any) => {
    const content = renderCellContent({ columnIndex, key, rowIndex, style });
    
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={columnIndex}
        key={key}
        parent={parent}
        rowIndex={rowIndex}
      >
        {content}
      </CellMeasurer>
    );
  };

  const renderCellContent = ({ columnIndex, key, rowIndex, style }: any) => {
    // Header row
    if (rowIndex === 0) {
      if (columnIndex === 0) {
        // Property header
        return (
          <div key={key} style={style} className="grid-header grid-header-property">
            Properties
          </div>
        );
      } else {
        // Date headers
        const dateColumn = dateColumns[columnIndex - 1];
        const isToday = dateColumn?.key === dateToLocalDateKey(new Date());
        const headerClass = `grid-header grid-header-date ${isToday ? 'grid-header-date-today' : ''}`;
        
        return (
          <div key={key} style={style} className={headerClass}>
            {dateColumn?.title || ''}
          </div>
        );
      }
    }

    // Data rows
    const propertyIndex = rowIndex - 1;
    const property = properties[propertyIndex];
    
    if (columnIndex === 0) {
      // Property name column
      const propertyName = property?.title || property?.name || `Property ${propertyIndex + 1}`;
      
      return (
        <div
          key={key}
          style={style}
          className={`property-cell property-cell-clickable ${rowIndex % 2 === 0 ? 'property-cell-even' : 'property-cell-odd'}`}
          onClick={() => handlePropertyClick(property)}
        >
          {propertyName}
        </div>
      );
    } else {
      // Date cells - show reservation if check-in matches this date
      const dateColumn = dateColumns[columnIndex - 1];
      const cellDateKey = dateColumn?.key; // YYYY-MM-DD format
      //console.log('cellDateKey LOOK HERE', cellDateKey);
      const cellReservations = filterCellReservations(reservations, property, dateColumn?.date);
      // console.log(dateColumn?.date)
      // console.log('cellReservations', cellReservations);
      const cellTasks = filterCellTasks(localTasks, property, cellDateKey);

      const checkinReservations = cellReservations.filter(
        (r: any) => cellDateKey === dateToLocalDateKey(r.checkin_field)
      );
      
      //console.log('checkinReservations', checkinReservations);
      
      const checkoutReservations = cellReservations.filter(
        (r: any) => cellDateKey === dateToLocalDateKey(r.checkout_field)
      );
      //console.log('checkout', checkoutReservations);
      const stayReservations = cellReservations.filter(
        (r: any) => {
          const checkin = dateToLocalDateKey(r.checkin_field);
          const checkout = dateToLocalDateKey(r.checkout_field);
          return cellDateKey > checkin && cellDateKey < checkout;
        }
      );

      const isToday = cellDateKey === dateToLocalDateKey(new Date());
      const cellClasses = [
        'date-cell',
        rowIndex % 2 === 0 ? 'date-cell-even' : 'date-cell-odd',
        isToday ? 'date-cell-today' : ''
      ].filter(Boolean).join(' ');

      return (
        <div key={key} style={style} className={cellClasses}>
          <DroppableCell id={`${property?.id}-${cellDateKey}`} activeTaskPropertyId={activeTask?.propertyId}>
            {(checkinReservations.length > 0 || checkoutReservations.length > 0 || stayReservations.length > 0 || cellTasks.length > 0) ? (
              <div className="date-cell-content">
              {/* Content container - groups reservations and tasks at top */}
              <div>
                {/* Handle complex overlapping scenarios */}
                {(() => {
                  const hasCheckins = checkinReservations.length > 0;
                  const hasCheckouts = checkoutReservations.length > 0;
                  const hasStays = stayReservations.length > 0;
                  
                  // If there are overlapping reservations with mixed types (stays + checkins/checkouts), use full width
                  if (hasStays && (hasCheckins || hasCheckouts)) {
                    const allReservations = [...checkinReservations, ...checkoutReservations, ...stayReservations];
                    return (
                      <FullWidthStackedReservations
                        allReservations={allReservations}
                        onReservationClick={handleReservationClick}
                        hoveredStayCell={hoveredStayCell}
                        cellId={`${property?.id}-${cellDateKey}`}
                        onMouseEnter={() => setHoveredStayCell(`${property?.id}-${cellDateKey}`)}
                        onMouseLeave={() => setHoveredStayCell(null)}
                        bubbleInstance={bubbleInstance}
                        onTooltipHover={handleTooltipHover}
                      />
                    );
                  }
                  
                  // Handle same-day check-in and check-out scenario (both present, no stays)
                  if (hasCheckins && hasCheckouts && !hasStays) {
                    return (
                      <SameDayReservations
                        checkinReservations={checkinReservations}
                        checkoutReservations={checkoutReservations}
                        onReservationClick={handleReservationClick}
                        bubbleInstance={bubbleInstance}
                        onTooltipHover={handleTooltipHover}
                      />
                    );
                  }
                  
                  // Handle individual reservation types
                  return (
                    <>
                      <CheckoutOnlyReservation
                        reservations={checkoutReservations}
                        onReservationClick={handleReservationClick}
                        bubbleInstance={bubbleInstance}
                        onTooltipHover={handleTooltipHover}
                      />
                      
                      <CheckinOnlyReservation
                        reservations={checkinReservations}
                        onReservationClick={handleReservationClick}
                        bubbleInstance={bubbleInstance}
                        onTooltipHover={handleTooltipHover}
                      />
                      
                      <StayReservation
                        reservations={stayReservations}
                        onReservationClick={handleReservationClick}
                        hoveredStayCell={hoveredStayCell}
                        cellId={`${property?.id}-${cellDateKey}`}
                        onMouseEnter={() => setHoveredStayCell(`${property?.id}-${cellDateKey}`)}
                        onMouseLeave={() => setHoveredStayCell(null)}
                        bubbleInstance={bubbleInstance}
                        onTooltipHover={handleTooltipHover}
                      />
                    </>
                  );
                })()}
                
                {/* Render tasks */}
                {cellTasks.map((task, index) => (
                  <DraggableTask
                    key={`task-${index}`}
                    task={task}
                    currentDate={cellDateKey}
                    propertyId={property?.id}
                    index={index}
                    departments={departments}
                    bubbleInstance={bubbleInstance}
                  />
                ))}
              </div>
              
              {/* Add Task Button - always at bottom */}
              <button 
                className="add-task-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddTaskClick(cellDateKey, property?.id);
                }}
              >
                + Task
              </button>
            </div>
          ) : (
            /* Show Add Task button even when cell is empty */
            <div className="date-cell-content">
              {/* Empty content container */}
              <div></div>
              
              {/* Add Task Button - always at bottom */}
              <button 
                className="add-task-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddTaskClick(cellDateKey, property?.id);
                }}
              >
                + Task
              </button>
            </div>
          )}
          </DroppableCell>
        </div>
      );
    }
  };

  const rowCount = properties.length + 1; // +1 for header
  const columnCount = dateColumns.length + 1; // +1 for property column

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="calendar-wrapper">
        <div className="calendar-container">
          
          <div className="calendar-grid">
            <AutoSizer>
              {({ height, width }) => (
                <MultiGrid
                  cellRenderer={cellRenderer}
                  columnCount={columnCount}
                  columnWidth={({ index }: { index: number }) => index === 0 ? propertyColumnWidth : 200}
                  height={height || 500}
                  rowCount={rowCount}
                  rowHeight={cache.rowHeight}
                  width={width || 1000}
                  fixedColumnCount={1}
                  fixedRowCount={1}
                  styleBottomRightGrid={{ outline: 'none' }}
                  styleTopRightGrid={{ outline: 'none' }}
                  styleBottomLeftGrid={{ outline: 'none' }}
                  styleTopLeftGrid={{ outline: 'none' }}
                />
              )}
            </AutoSizer>
          </div>
       
        </div>
      </div>
      
      <DragOverlay>
        {activeTask && (
          <TaskDragOverlay 
            task={activeTask.task} 
            departments={departments} 
          />
        )}
      </DragOverlay>
      
      {/* Global Reservation Tooltip */}
      {tooltipData && (
        <div 
          className="global-reservation-tooltip"
          style={{
            position: 'fixed',
            top: tooltipData.y + 10,
            left: tooltipData.x + 10,
            zIndex: 10000,
            pointerEvents: 'none',
            backgroundColor: 'var(--color-white)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-stroke-weak)',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          <div style={{ fontWeight: '600', marginBottom: '8px', color: 'var(--color-text)' }}>
            {tooltipData.reservations.length > 1 
              ? `${tooltipData.reservations.length} ${tooltipData.type === 'checkin' ? 'Check-ins' : tooltipData.type === 'checkout' ? 'Check-outs' : 'Reservations'}`
              : `${tooltipData.type === 'checkin' ? 'Check-in' : tooltipData.type === 'checkout' ? 'Check-out' : 'Reservation'}`
            }
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {tooltipData.reservations.map((reservation, index) => {
              const checkinDate = reservation.checkin_field;
              const checkoutDate = reservation.checkout_field;
              
              const formatDateTime = (dateString: string) => {
                if (!dateString) return 'N/A';
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  year: 'numeric'
                }) + ' at ' + date.toLocaleTimeString('en-US', { 
                  hour: 'numeric', 
                  minute: '2-digit',
                  hour12: true 
                });
              };

              const getReservationStatus = (reservation: any) => {
                const today = dateToLocalDateKey(new Date());
                const checkinDateKey = dateToLocalDateKey(new Date(reservation.checkin_field));
                const checkoutDateKey = dateToLocalDateKey(new Date(reservation.checkout_field));
                
                if (today >= checkinDateKey && today <= checkoutDateKey) {
                  return { label: 'in-stay', color: 'var(--color-success)' };
                } else if (today > checkoutDateKey) {
                  return { label: 'past', color: 'var(--color-text-weak)' };
                } else {
                  return { label: 'future', color: 'var(--color-blue)' };
                }
              };

              const status = getReservationStatus(reservation);

              return (
                <div key={index} style={{ fontSize: '11px', lineHeight: '1.4' }}>
                  <div style={{ fontWeight: '600', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{reservation.guest_name || 'Guest'}</span>
                    <span style={{ 
                      fontSize: '9px', 
                      color: status.color, 
                      fontWeight: '500',
                      opacity: 0.8
                    }}>
                      ({status.label})
                    </span>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--color-text-weak)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: 'var(--color-success)', fontSize: '12px' }}>→</span>
                      Check-in: {formatDateTime(checkinDate)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: 'var(--color-warning)', fontSize: '12px' }}>←</span>
                      Check-out: {formatDateTime(checkoutDate)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DndContext>
  );
}